import React, { useState } from "react";
import _ from "lodash";
import { SubscriptionCancelModal } from "./SubscriptionCancelModal";

const SubscriptionHistoryModal = ({
  loading = false,
  histories,
  invoices,
  currency,
  planId,
  subscriptionCancel,
}) => {
  function formatDateToDDMMYYYY(dateString) {
    if (!dateString) return "—";
    const date = new Date(dateString?.replace(" ", "T"));
    if (isNaN(date.getTime())) return "—";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const groupedHistories = _.groupBy(histories, "subscription_id");

  // Modal State for Cancellation
  const [cancelModalState, setCancelModalState] = useState({
    show: false,
    subscriptionId: "",
    paymentMethod: "",
    kids: [],
    renewalDate: "",
    planName: "Subscription Plan",
  });

  const handleOpenCancelModal = (subscriptionId, group) => {
    const kidNames = group.map((h) => h.kid_name || h.username).filter(Boolean);
    const renewalDate = formatDateToDDMMYYYY(group[0]?.renewal_date);
    const paymentMethod = group[0]?.payment_method || "stripe";

    setCancelModalState({
      show: true,
      subscriptionId,
      paymentMethod,
      kids: kidNames,
      renewalDate,
      planName: "Yuva Quest Annual Plan",
    });
  };

  const handleCloseCancelModal = () => {
    setCancelModalState((prev) => ({ ...prev, show: false }));
  };

  if (loading) {
    return (
      <div className="mt-4">
        <h6 className="fw-bold text-dark mb-3 placeholder-glow">
          <span
            className="placeholder col-3 bg-secondary-subtle rounded"
            style={{ height: "18px" }}
          ></span>
        </h6>
        <div className="row g-3 g-sm-4">
          {[1, 2].map((idx) => (
            <div className="col-12 col-lg-6" key={idx}>
              <div
                className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden"
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #EAECEF",
                }}
              >
                {/* Header Skeleton */}
                <div
                  className="p-3 px-3 px-sm-4 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 border-bottom placeholder-glow"
                  style={{ backgroundColor: "#F9FAFB" }}
                >
                  <div className="w-100 w-sm-50">
                    <span
                      className="placeholder col-8 bg-secondary-subtle rounded d-block mb-1"
                      style={{ height: "16px" }}
                    ></span>
                    <span
                      className="placeholder col-5 bg-secondary-subtle rounded d-block"
                      style={{ height: "12px" }}
                    ></span>
                  </div>
                  <div className="d-flex gap-2 flex-wrap justify-content-start justify-content-sm-end">
                    <span
                      className="placeholder col-5 rounded-pill bg-secondary-subtle"
                      style={{ height: "22px", width: "50px" }}
                    ></span>
                    <span
                      className="placeholder col-5 rounded-pill bg-secondary-subtle"
                      style={{ height: "22px", width: "60px" }}
                    ></span>
                  </div>
                </div>

                {/* Body Skeleton */}
                <div className="card-body p-3 p-sm-4 placeholder-glow">
                  <div className="row g-3">
                    <div className="col-12 mb-2">
                      <span
                        className="placeholder col-4 bg-secondary-subtle rounded d-block mb-2"
                        style={{ height: "14px" }}
                      ></span>
                      <div className="d-flex gap-2">
                        <span
                          className="placeholder col-3 rounded-pill bg-secondary-subtle"
                          style={{ height: "24px" }}
                        ></span>
                        <span
                          className="placeholder col-3 rounded-pill bg-secondary-subtle"
                          style={{ height: "24px" }}
                        ></span>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <span
                        className="placeholder col-6 bg-secondary-subtle rounded d-block mb-1"
                        style={{ height: "12px" }}
                      ></span>
                      <span
                        className="placeholder col-8 bg-secondary-subtle rounded d-block"
                        style={{ height: "16px" }}
                      ></span>
                    </div>
                    <div className="col-sm-6">
                      <span
                        className="placeholder col-6 bg-secondary-subtle rounded d-block mb-1"
                        style={{ height: "12px" }}
                      ></span>
                      <span
                        className="placeholder col-8 bg-secondary-subtle rounded d-block"
                        style={{ height: "16px" }}
                      ></span>
                    </div>
                    <div className="col-12 pt-2 border-top d-flex justify-content-between align-items-center">
                      <span
                        className="placeholder col-3 bg-secondary-subtle rounded"
                        style={{ height: "14px" }}
                      ></span>
                      <span
                        className="placeholder col-2 bg-secondary-subtle rounded"
                        style={{ height: "22px" }}
                      ></span>
                    </div>
                  </div>
                </div>

                {/* Footer Skeleton */}
                <div className="p-3 px-3 px-sm-4 bg-light border-top d-flex flex-wrap justify-content-between align-items-center gap-2 placeholder-glow sub-card-footer">
                  <span
                    className="placeholder col-5 rounded-pill bg-secondary-subtle"
                    style={{ height: "32px", minWidth: "120px" }}
                  ></span>
                  <span
                    className="placeholder col-5 rounded-pill bg-secondary-subtle"
                    style={{ height: "32px", minWidth: "120px" }}
                  ></span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!histories || Object.entries(groupedHistories).length === 0) {
    return (
      <div className="text-center py-5 bg-white rounded-4 border border-dashed my-4">
        <i className="bi bi-receipt fs-1 text-secondary opacity-50 mb-3 d-block" />
        <h6 className="fw-bold text-dark">No Active or Past Subscriptions Found</h6>
        <p className="text-muted small mb-0">
          When you purchase or renew a subscription for your kids, it will appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="mt-4">
        <h6 className="fw-bold text-dark mb-3">Subscription Details & History</h6>
        <div className="row g-3 g-sm-4">
          {Object.entries(groupedHistories).map(([subscriptionId, group], idx) => {
            const matchingInvoice = invoices?.find(
              (inv) => inv.external_subscription_id === subscriptionId
            );

            const combinedKidNames = group
              .map((h) => h.kid_name || h.username)
              .filter(Boolean)
              .join(", ");

            const totalAmount = group.reduce((sum, h) => {
              return (
                sum +
                Number(h?.coupon?.after_discount_unit_price ?? h?.unit_price ?? 0)
              );
            }, 0);

            const status = group[0]?.status || "Unknown";
            const isActive = status === "Active";
            const isCancelled = status === "Cancelled";
            const hasActiveTrial = group.some((kid) => kid.trial?.status === "Active");
            const paymentMethod = group[0]?.payment_method || "stripe";
            const isCancellable = isActive && Boolean(subscriptionId);

            return (
              <div className="col-12 col-lg-6" key={subscriptionId || idx}>
                <div
                  className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden"
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #EAECEF",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {/* Card Header */}
                  <div
                    className="p-3 px-3 px-sm-4 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 border-bottom"
                    style={{ backgroundColor: "#F9FAFB" }}
                  >
                    <div>
                      <span className="fw-bold text-dark fs-6 d-block">Yuva Quest Annual Plan</span>
                      <div className="text-muted small">
                        Invoice:{" "}
                        <span className="fw-medium text-secondary">
                          {matchingInvoice?.invoice_number ?? "Pending / Direct"}
                        </span>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-1 gap-sm-2">
                      {/* Kids Covered Pill */}
                      <span
                        className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1 fw-semibold"
                        style={{ fontSize: "11px" }}
                      >
                        <i className="bi bi-people-fill me-1" />
                        {group.length} {group.length > 1 ? "Kids" : "Kid"}
                      </span>

                      {/* Payment Method Badge */}
                      <span
                        className="badge bg-light text-dark border px-2 py-1 text-capitalize fw-semibold"
                        style={{ fontSize: "11px", letterSpacing: "0.3px" }}
                      >
                        <i className="bi bi-credit-card me-1 text-primary" />
                        {paymentMethod}
                      </span>

                      {/* Status Badge */}
                      {isActive ? (
                        hasActiveTrial ? (
                          <span
                            className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle px-2 py-1 fw-semibold"
                            style={{ fontSize: "11px" }}
                          >
                            <i className="bi bi-clock-history me-1" />
                            Free Trial
                          </span>
                        ) : (
                          <span
                            className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 fw-semibold"
                            style={{ fontSize: "11px" }}
                          >
                            <i className="bi bi-check-circle-fill me-1" />
                            Active
                          </span>
                        )
                      ) : isCancelled ? (
                        <span
                          className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1 fw-semibold"
                          style={{ fontSize: "11px" }}
                        >
                          <i className="bi bi-x-circle-fill me-1" />
                          Cancelled
                        </span>
                      ) : (
                        <span
                          className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2 py-1 fw-semibold"
                          style={{ fontSize: "11px" }}
                        >
                          {status}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="card-body p-3 p-sm-4">
                    <div className="row g-3">
                      {/* Kid(s) Covered */}
                      <div className="col-12">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <i className="bi bi-people text-primary fs-6" />
                          <span className="text-secondary small fw-medium">
                            Kids Covered ({group.length}):
                          </span>
                        </div>
                        <div className="d-flex flex-wrap gap-1 ps-2 ps-sm-4">
                          {group.map((kid, kIdx) => (
                            <span
                              key={kIdx}
                              className="badge rounded-pill bg-light text-dark border px-2 py-1 fw-medium"
                              style={{ fontSize: "12px" }}
                            >
                              {kid.kid_name || kid.username || `Kid #${kIdx + 1}`}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Billing Dates */}
                      <div className="col-sm-6">
                        <span className="text-secondary small d-block mb-1">Started Date</span>
                        <div className="fw-semibold text-dark small d-flex align-items-center gap-1">
                          <i className="bi bi-calendar-check text-muted" />
                          {formatDateToDDMMYYYY(group[0]?.payment_date)}
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <span className="text-secondary small d-block mb-1">
                          {isCancelled ? "Access Valid Until" : "Next Renewal Date"}
                        </span>
                        <div className="fw-semibold text-dark small d-flex align-items-center gap-1">
                          <i className="bi bi-calendar-event text-primary" />
                          {formatDateToDDMMYYYY(group[0]?.renewal_date)}
                        </div>
                      </div>

                      {/* Amount Paid */}
                      <div className="col-12 pt-2 border-top">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="text-secondary small fw-medium d-block">
                              Amount Paid:
                            </span>
                            {group.length > 1 && (
                              <span className="text-muted" style={{ fontSize: "11px" }}>
                                Combined recurring for {group.length} kids
                              </span>
                            )}
                          </div>
                          <span className="fw-bold text-dark fs-5">
                            {currency === "USD" ? "$" : "₹"}
                            {totalAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer Actions */}
                  <div
                    className="p-3 px-3 px-sm-4 d-flex flex-wrap justify-content-between align-items-center gap-2 border-top bg-light-subtle sub-card-footer"
                  >
                    <div>
                      {matchingInvoice?.invoice_file_url ? (
                        <a
                          href={matchingInvoice.invoice_file_url}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-secondary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1"
                          style={{ fontSize: "12px" }}
                        >
                          <i className="bi bi-download" />
                          Download Invoice
                        </a>
                      ) : (
                        <span className="text-muted small fst-italic" style={{ fontSize: "12px" }}>
                          Invoice available via gateway
                        </span>
                      )}
                    </div>

                    <div>
                      {isCancellable && (
                        <button
                          className="btn btn-outline-danger btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1"
                          style={{ fontSize: "12px", fontWeight: 500 }}
                          onClick={() => handleOpenCancelModal(subscriptionId, group)}
                        >
                          <i className="bi bi-x-circle" />
                          Cancel Subscription
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cancellation Modal */}
      <SubscriptionCancelModal
        show={cancelModalState.show}
        onHide={handleCloseCancelModal}
        subscriptionId={cancelModalState.subscriptionId}
        paymentMethod={cancelModalState.paymentMethod}
        kids={cancelModalState.kids}
        renewalDate={cancelModalState.renewalDate}
        planName={cancelModalState.planName}
        onSuccess={subscriptionCancel}
      />
    </>
  );
};

export default SubscriptionHistoryModal;